import React, { useEffect } from 'react';
import { Box, ClickAwayListener, IconButton, Popper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import { useTranslation } from 'react-i18next';

import type { HotspotPlacementVariant } from '../hotspotsUnreal';

const usePopoverStyles = () => ({
  paper: {
    width: 220,
    background:
      'linear-gradient(135deg, rgba(32, 32, 40, 0.97), rgba(18, 18, 24, 0.97))',
    backdropFilter: 'blur(18px)',
    border: '1px solid rgba(255, 255, 255, 0.13)',
    borderRadius: '16px',
    boxShadow: '0 24px 70px rgba(0, 0, 0, 0.6)',
    padding: '12px',
    color: '#e9eaf1',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: '12px',
    fontWeight: 750,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '9px',
    color: '#e9eaf1',
    '& > svg': {
      color: '#b9aef8',
      fontSize: 14,
    },
  },
  closeBtn: {
    color: '#969aa8',
    padding: '2px',
    marginLeft: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: '#dfe1e9',
    borderRadius: '9px',
    padding: '9px 8px',
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontFamily: 'inherit',
    fontSize: '12px',
    fontWeight: 600,
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
    '& > svg': { color: '#969aa8', fontSize: 16, flex: 'none' },
  },
});

interface IAddHotspotPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onPick: (variant: HotspotPlacementVariant) => void;
}

const AddHotspotPopover = ({
  open,
  anchorEl,
  onClose,
  onPick,
}: IAddHotspotPopoverProps) => {
  const { t: translate } = useTranslation();
  const classes = usePopoverStyles();

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="right-start"
      modifiers={[
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'preventOverflow', options: { padding: 12 } },
      ]}
      sx={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Box sx={classes.paper} role="dialog" aria-modal="false">
          <Box sx={classes.title}>
            <AddCircleOutlineOutlinedIcon />
            {translate('editorMode.addHotspot', 'Add hotspot')}
            <IconButton
              size="small"
              sx={classes.closeBtn}
              onClick={onClose}
              aria-label={translate('editorMode.close')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="button"
            type="button"
            sx={classes.row}
            onClick={() => onPick('2D')}
          >
            <ImageOutlinedIcon />
            {translate('editorMode.hotspot2D', '2D panel')}
          </Box>
          <Box
            component="button"
            type="button"
            sx={classes.row}
            onClick={() => onPick('3D')}
          >
            <ViewInArOutlinedIcon />
            {translate('editorMode.hotspot3D', '3D object')}
          </Box>
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};

export default AddHotspotPopover;
